import { useCallback } from 'react';
import { Platform } from 'react-native';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { incrementFilterVersion, triggerFilterChanged } from './useInfiniteListUtils';
import { PostsService } from '@/services/domain/PostsService';
import { Post } from '@/models/Post';
import { plainToClass } from '@/models/transform';

interface UseQueryCacheUtilsReturn {
  removeCacheItem: (
    queryKeyPrefix: string,
    itemId: string | number,
    itemsKey?: string
  ) => void;
  updateCacheItem: (
    queryKeyPrefix: string,
    itemId: string | number,
    changes: Record<string, any>,
    itemsKey?: string
  ) => void;
  updateSingleQuery: (
    queryKey: unknown[] | string,
    changes: Record<string, any>
  ) => void;
  syncPostFromServer: (postId: number) => Promise<void>;
}

/**
 * Utility hook for managing query cache operations on infinite query data.
 * Provides standardized functionality for:
 * - Removing items from cache across all related queries
 * - Updating item properties in cache across all related queries
 * 
 * Works with infinite query data structure that has:
 * - pages: array of page data
 * - each page contains an array of items (default key: 'Items', or specify custom)
 * - flattened array named 'items' (standardized convention)
 */
export function useQueryCacheUtils(): UseQueryCacheUtilsReturn {
  const queryClient = useQueryClient();

  const removeCacheItem = useCallback(
    (queryKeyPrefix: string, itemId: string | number, itemsKey = 'Items') => {
      const queryCache = queryClient.getQueryCache();
      const queries = queryCache.findAll({
        queryKey: [queryKeyPrefix],
        exact: false,
      });

      queries.forEach((query) => {
        queryClient.setQueryData(query.queryKey, (oldData: any) => {
          if (!oldData?.pages) return oldData;

          let removedCount = 0;
          const newPages = oldData.pages.map((page: any) => {
            const oldItems = page[itemsKey] ?? [];
            const newItems = oldItems.filter((item: any) => item.Id !== itemId);
            removedCount += oldItems.length - newItems.length;
            return {
              ...page,
              [itemsKey]: newItems,
            };
          });

          const pagesWithCount =
            removedCount > 0
              ? newPages.map((page: any) => {
                  if (typeof page.ItemCount === 'number') {
                    return {
                      ...page,
                      ItemCount: Math.max(0, page.ItemCount - removedCount),
                    };
                  }
                  return page;
                })
              : newPages;

          const updates: any = {
            ...oldData,
            pages: pagesWithCount,
          };

          if (oldData.items) {
            updates.items = pagesWithCount.flatMap((page: any) => page[itemsKey] ?? []);
          }

          return updates;
        });
      });
    },
    [queryClient]
  );

  const updateCacheItem = useCallback(
    (
      queryKeyPrefix: string,
      itemId: string | number,
      changes: Record<string, any>,
      itemsKey = 'Items'
    ) => {
      const queryCache = queryClient.getQueryCache();
      const queries = queryCache.findAll({
        queryKey: [queryKeyPrefix],
        exact: false,
      });

      queries.forEach((query) => {
        queryClient.setQueryData(query.queryKey, (oldData: any) => {
          if (!oldData?.pages) return oldData;

          const newPages = oldData.pages.map((page: any) => ({
            ...page,
            [itemsKey]: page[itemsKey].map((item: any) => {
              if (item.Id === itemId) {
                return { ...item, ...changes };
              }
              return item;
            }),
          }));

          const updates: any = {
            ...oldData,
            pages: newPages,
          };

          if (oldData.items) {
            updates.items = newPages.flatMap((page: any) => page[itemsKey]);
          }

          return updates;
        });
      });
    },
    [queryClient]
  );

  const updateSingleQuery = useCallback(
    (queryKey: unknown[] | string, changes: Record<string, any>) => {
      const queryCache = queryClient.getQueryCache();
      const isExact = Array.isArray(queryKey);
      const key = Array.isArray(queryKey) ? queryKey : [queryKey];

      const queries = queryCache.findAll({ queryKey: key as any, exact: isExact });

      queries.forEach((query) => {
        queryClient.setQueryData(query.queryKey, (oldData: any) => {
          if (!oldData || typeof oldData !== 'object') return oldData;
          return { ...oldData, ...changes };
        });
      });
    },
    [queryClient]
  );

  const syncPostFromServer = useCallback(
    async (postId: number) => {
      const fresh = await PostsService.getPost(postId);
      const patch = {
        CommentsCount: fresh.CommentsCount,
        RecentComments: fresh.RecentComments,
      };

      queryClient.setQueryData(['post', postId], (old: Post | undefined) =>
        old ? plainToClass(Post, { ...old, ...patch }) : old
      );

      const queries = queryClient.getQueryCache().findAll({
        queryKey: ['posts'],
        exact: false,
      });

      for (const q of queries) {
        queryClient.setQueryData(q.queryKey, (oldData: any) => {
          if (!oldData?.pages) return oldData;
          const newPages = oldData.pages.map((page: any) => ({
            ...page,
            PostItems:
              page.PostItems?.map((p: Post) =>
                p.Id === postId ? plainToClass(Post, { ...p, ...patch }) : p
              ) ?? page.PostItems,
          }));
          const updates: any = { ...oldData, pages: newPages };
          if (oldData.items) {
            updates.items = newPages.flatMap((page: any) => page.PostItems ?? []);
          }
          return updates;
        });
      }
    },
    [queryClient]
  );

  return { removeCacheItem, updateCacheItem, updateSingleQuery, syncPostFromServer };
}

/**
 * Stable handler that removes a deleted post from all post list caches.
 * Shared by every screen that renders PostCard lists (feed, profile wall).
 */
export function useRemovePostFromCache(): (postId: number) => void {
  const { removeCacheItem } = useQueryCacheUtils();
  return useCallback((postId: number) => removeCacheItem('posts', postId, 'PostItems'), [removeCacheItem]);
}

function clearAllStoredScrollPositions() {
  if (Platform.OS !== 'web' || typeof window === 'undefined' || !window.sessionStorage) return;
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith('scroll_offset_') || key?.startsWith('scroll_index_')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // Silently handle errors
  }
}

/**
 * Invalidates all list queries affected by a global filter change (e.g. My Type Filter).
 * - Inactive queries are removed from cache (fetched fresh when navigated to)
 * - Active queries use deferred reset: only the focused screen resets immediately;
 *   others reset when they gain focus
 * - Stored scroll positions are cleared so lists start from the top
 */
export function invalidateFilteredListQueries(queryClient: QueryClient) {
  const prefixes = [['posts'], ['members'], ['photos'], ['discovery']];

  for (const prefix of prefixes) {
    queryClient.removeQueries({ queryKey: prefix, type: 'inactive' });
  }

  queryClient.invalidateQueries({ queryKey: ['members', 'online', 'rightPanel'], exact: true });
  queryClient.invalidateQueries({ queryKey: ['members', 'visitors', 'rightPanel'], exact: true });

  incrementFilterVersion();
  triggerFilterChanged();

  clearAllStoredScrollPositions();
}